import { Search, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch, Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import { AppointmentStatusLabel } from "@/features/appointment-status";
import { BookCallbackForm } from "@/features/book-callback";
import { DayAndTime, ymd } from "@/features/day-and-time";
import { UserSearchMore, useUserSearch } from "@/features/user-search";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { bookingAttempt, confirmationLeft, confirmationScheduled, defaultHost, timeRange, type BookingAttempt } from "@/lib/appointments";
import { chosenTime, forCalendar, openWhen, withSlot, type When } from "@/lib/booking-time";
import type { PickedTime } from "@/lib/calendar-view";
import { localDay, staffName } from "@/lib/calendars";
import { withPicked } from "@/lib/paging";
import {
  useBookAppointment, useCalendars, useContact, useContacts, useCreateCalendarBlock, useMainCalendar,
} from "@/lib/queries";
import { sendFailure } from "@/lib/texts";
import { atLeast, type Appointment, type ContactRow } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";

const contactName = (c: Pick<ContactRow, "first_name" | "last_name" | "phone">) =>
  [c.first_name, c.last_name].filter(Boolean).join(" ") || c.phone;

const newKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/** Who an appointment is for, as the form shows them. */
export interface BookingContact {
  id: string;
  name: string;
  phone: string | null;
  /** Their clock, offered as a zone to book in and shown beside the time picked (ACL-521). */
  timezone?: string | null;
}

/** Where the picker opens for a click on the calendar: the day clicked, read on the calendar's
 *  clock when a time was clicked too, which a custom time then starts from. */
function openOn(timezone: string, picked: PickedTime | null): When {
  if (!picked) return openWhen(timezone);
  if (!picked.withTime) return openWhen(timezone, { day: ymd(picked.at) });
  const at = picked.at.toISOString();
  return openWhen(timezone, { day: localDay(at, timezone), from: at });
}

export type NewTab = "callback" | "appointment" | "block";

const NEW_TITLES: Record<NewTab, string> = {
  callback: "Book a callback",
  appointment: "Book appointment",
  block: "Add blocked off time",
};

/**
 * GoHighLevel's "Book appointment" dialog (ACL-292): an appointment for a contact, or time blocked
 * off. Every appointment goes on the tenant's main calendar (ACL-655, ADR-215), so nobody picks one.
 * Nor a person: a rep hosts what they book, and otherwise core-api chooses who is free. An appointment
 * is booked on a day and one of that day's free times, or at a custom time (ACL-521), read in the calendar's timezone unless
 * the booker picks another, and starts confirmed unless changed. The contact is required here,
 * as it is in GoHighLevel. Opened from a click on the calendar, it starts on that day — and, from
 * the hour grids, at that time when it is free (`picked`).
 *
 * Opened from a conversation (ACL-294), `contact` is the thread's and cannot be changed, `source`
 * says where it was booked, and there is no time to block off: the dialog books the contact, through
 * the same free times and rules as the Calendars page.
 *
 * A callback (ACL-659) is the form Book a callback has on the contact page, with a contact to search
 * for on top. A time clicked on the calendar is the same instant on the lead's clock there.
 *
 * The rep chooses what the contact is texted (ACL-504): a confirmation once it is booked, off unless
 * turned on, and reminders before it, on unless turned off.
 */
export function NewAppointmentDialog({
  open, onOpenChange, onBooked, tab: initialTab = "appointment", picked, contact, source,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBooked?: (id: number) => void;
  tab?: NewTab;
  picked?: PickedTime | null;
  /** The contact to book, fixed. */
  contact?: BookingContact | null;
  /** Where the booking comes from, as core-api keeps it; `manual` when not given. */
  source?: Appointment["source"];
}) {
  const [tab, setTab] = React.useState<NewTab>(initialTab);
  const forContact = contact != null;
  React.useEffect(() => {
    if (open) setTab(forContact ? "appointment" : initialTab);
  }, [open, initialTab, forContact]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="lg">
        <DialogHeader>
          <DialogTitle>{NEW_TITLES[tab]}</DialogTitle>
        </DialogHeader>
        {!contact && (
          <Tabs value={tab} onValueChange={(t) => setTab(t as NewTab)} className="shrink-0 px-5 pt-3">
            <TabsList>
              <TabsTrigger value="callback">Callback</TabsTrigger>
              <TabsTrigger value="appointment">Appointment</TabsTrigger>
              <TabsTrigger value="block">Blocked off time</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {open && tab === "callback" && <CallbackTab picked={picked ?? null} onClose={() => onOpenChange(false)} />}
        {open && tab === "appointment" && (
          <BookForm
            picked={picked ?? null}
            fixed={contact ?? null}
            source={source}
            onClose={() => onOpenChange(false)}
            onBooked={onBooked}
          />
        )}
        {open && tab === "block" && <BlockForm picked={picked ?? null} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

/** Who a booking is for: search for them, or, once chosen, their name and a way to change it. */
function ContactField({
  contact, onChange, fixed = false,
}: {
  contact: BookingContact | null;
  onChange: (contact: BookingContact | null) => void;
  fixed?: boolean;
}) {
  const [query, setQuery] = React.useState("");
  const q = useDebounced(query.trim());
  const contacts = useContacts({ q: q || undefined }, 0, !fixed && q.length >= 2);
  const matches = q.length >= 2 ? (contacts.data?.items ?? []).slice(0, 6) : [];
  return (
    <Field label="Contact" htmlFor="book-contact" required>
      {contact ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5">
          <span className="min-w-0 text-sm">
            <span className="block truncate font-medium text-text">{contact.name}</span>
            {contact.phone && <span className="block text-xs text-text-muted">{formatPhoneNumber(contact.phone)}</span>}
          </span>
          {!fixed && (
            <Button type="button" variant="ghost" size="icon-sm" aria-label="Change contact" onClick={() => onChange(null)}>
              <X />
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <Input
            id="book-contact"
            leading={<Search className="size-3.5" />}
            value={query}
            placeholder="Search by name, email, or phone"
            onChange={(e) => setQuery(e.target.value)}
          />
          {matches.length > 0 && (
            <ul className="divide-y divide-border rounded-md border border-border">
              {matches.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
                    onClick={() => onChange({ id: m.id, name: contactName(m), phone: m.phone, timezone: m.timezone })}
                  >
                    <span className="truncate text-text">{contactName(m)}</span>
                    <span className="shrink-0 text-xs text-text-muted">{formatPhoneNumber(m.phone)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q.length >= 2 && !contacts.isFetching && matches.length === 0 && (
            <p className="text-xs text-text-muted">No contact matches.</p>
          )}
        </div>
      )}
    </Field>
  );
}

/** Book a callback from the calendar: a contact, then the contact page's form, opened on their card. */
function CallbackTab({ picked, onClose }: { picked: PickedTime | null; onClose: () => void }) {
  const [contact, setContact] = React.useState<BookingContact | null>(null);
  const card = useContact(contact?.id);
  return (
    <>
      <div className="shrink-0 px-5 pt-3">
        <ContactField contact={contact} onChange={setContact} />
      </div>
      {contact && card.error ? (
        <div className="px-5 pt-3">
          <Callout tone="danger">{card.error instanceof ApiError ? card.error.message : "Could not open the contact."}</Callout>
        </div>
      ) : contact && card.data?.id === contact.id ? (
        <BookCallbackForm key={contact.id} contact={card.data} picked={picked} onClose={onClose} />
      ) : contact ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-4" />
        </div>
      ) : null}
      {!(contact && card.data?.id === contact.id) && (
        <DialogFooter className="mt-auto">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled title="Select a contact">
            Book callback
          </Button>
        </DialogFooter>
      )}
    </>
  );
}

function BookForm({
  picked, fixed, source, onClose, onBooked,
}: {
  picked: PickedTime | null;
  fixed: BookingContact | null;
  source?: Appointment["source"];
  onClose: () => void;
  onBooked?: (id: number) => void;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const main = useMainCalendar();
  const chosen = main.data ?? null;
  const book = useBookAppointment();

  const [title, setTitle] = React.useState("");
  const [describing, setDescribing] = React.useState(false);
  const [notes, setNotes] = React.useState("");
  // The time, once there is a calendar to read it on (`lib/booking-time.ts`).
  const [when, setWhen] = React.useState<When | null>(null);
  const [status, setStatus] = React.useState<"confirmed" | "scheduled">("confirmed");
  const [confirming, setConfirming] = React.useState(false);
  const [reminders, setReminders] = React.useState(true);
  const [contact, setContact] = React.useState<BookingContact | null>(fixed);
  const [error, setError] = React.useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = React.useState(false);
  // One key per booking request, kept while the request is the same (`bookingAttempt`), so a press
  // after an answer that never came cannot book twice. Held from the press rather than from the
  // mutation's next render, so a double press sends one request.
  const attempt = React.useRef<BookingAttempt | null>(null);
  const inFlight = React.useRef(false);

  // The calendar opens on its clock.
  React.useEffect(() => {
    if (chosen) setWhen((w) => (w ? forCalendar(w, chosen.timezone) : openOn(chosen.timezone, picked)));
  }, [chosen?.id, chosen?.timezone]);
  const changeWhen = (change: (w: When) => When) => setWhen((w) => (w ? change(w) : w));
  const time = chosenTime(when, Date.now());
  const activeStaff = chosen?.staff.filter((s) => s.user_active) ?? [];
  // Nobody picks a person (ACL-655): a rep hosts what they book, and otherwise core-api chooses.
  const member = defaultHost(user, activeStaff);
  const ready = Boolean(chosen && time && contact);

  const submit = async () => {
    if (!chosen || !when || !time || !contact || inFlight.current) return;
    const request = {
      calendarId: chosen.id,
      start: time.start,
      end: time.end,
      lead_id: contact.id,
      title: title.trim() || null,
      notes: notes.trim() || null,
      status,
      user_id: member ?? undefined,
      source,
    };
    // What the contact is texted is not another booking: a press after an answer that never came
    // keeps its key when only these changed, and core-api sends a confirmation not sent yet.
    const texts = { send_confirmation: confirming, send_reminders: reminders };
    const sent = bookingAttempt(attempt.current, request, newKey);
    attempt.current = sent;
    inFlight.current = true;
    setError(null);
    setUnconfirmed(false);
    try {
      const made = await book.mutateAsync({ ...request, ...texts, idempotency_key: sent.key });
      const withWhom = chosen.staff.find((s) => s.user_id === made.assigned_user_id);
      const confirmation = made.confirmation;
      // The time as it was picked: on the zone the booker read it in.
      const at = timeRange(made.starts_at, made.ends_at, when.zone, chosen.time_format, chosen.locale);
      toast.success(
        "Appointment booked",
        [
          `${withWhom ? `${made.title}, with ${staffName(withWhom)}` : made.title} · ${at}`,
          confirmation && confirmationLeft(confirmation) ? "Confirmation text sent." : null,
          confirmation && confirmationScheduled(confirmation)
            ? `Confirmation text goes out ${confirmation.send_at ? formatDateTime(confirmation.send_at) : "later"}, the contact's 8 am.`
            : null,
        ].filter(Boolean).join(". "),
      );
      // The booking stands; the text did not leave, and the rep can text from the conversation.
      if (confirmation && !confirmationLeft(confirmation) && !confirmationScheduled(confirmation)) {
        toast.info("Confirmation text not sent", confirmation.problem ?? undefined);
      }
      onClose();
      onBooked?.(made.id);
    } catch (err) {
      // A 4xx is core-api turning the booking down before it made anything: say why, and let the
      // time be picked again (a custom time stays, to be changed). Anything else says nothing
      // either way (`sendFailure`): the form and its key stay, so pressing again asks core-api for
      // the same booking, never a second one.
      const failure = sendFailure(err);
      if (failure.kind === "refused") {
        setError(failure.detail);
        attempt.current = null;
        changeWhen((w) => withSlot(w, null));
      } else {
        setUnconfirmed(true);
      }
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <>
      <DialogBody className="space-y-4">
        {error && <Callout tone="danger">{error}</Callout>}
        {unconfirmed && (
          <Callout tone="warning">
            We could not confirm the appointment was booked. Press Book appointment again: the same booking is sent, so it
            is never made twice.
          </Callout>
        )}
        {main.error && (
          <Callout tone="danger">
            Could not open the calendar: {main.error instanceof ApiError ? main.error.message : "please try again."}{" "}
            <button type="button" className="underline" onClick={() => void main.refetch()}>
              Try again
            </button>
          </Callout>
        )}
        {chosen && activeStaff.length === 0 && <Callout tone="warning">No rep can be booked yet.</Callout>}

        <ContactField contact={contact} onChange={setContact} fixed={fixed !== null} />

        <Field label="Appointment title" htmlFor="book-title" hint={`Empty: ${chosen?.name ?? "Appointments"} and the contact's name.`}>
          <Input
            id="book-title"
            value={title}
            maxLength={200}
            placeholder={chosen ? `${chosen.name} – ${contact ? contact.name : "{contact}"}` : ""}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        {describing ? (
          <Field label="Description" htmlFor="book-notes">
            <Textarea id="book-notes" rows={3} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} autoFocus />
          </Field>
        ) : (
          <button type="button" className="block text-sm font-medium text-accent-11 hover:underline" onClick={() => setDescribing(true)}>
            + Add description
          </button>
        )}

        {chosen && when ? (
          <Field label="Date and time" htmlFor="book-zone" required>
            <DayAndTime
              id="book-zone"
              calendar={chosen}
              when={when}
              onWhen={changeWhen}
              wanted={picked?.withTime && when.day === localDay(picked.at.toISOString(), when.zone) ? picked.at : null}
              userId={member}
              contactZone={contact?.timezone}
            />
          </Field>
        ) : (
          <p className="rounded-lg bg-gray-2 p-3 text-sm text-text-muted">Loading the free times.</p>
        )}

        <div className="space-y-2">
          <TextChoice
            id="book-confirmation"
            title="Text a confirmation"
            description={`Texted to ${contact?.name ?? "the contact"} once it is booked.`}
            checked={confirming}
            onChange={setConfirming}
          />
          <TextChoice
            id="book-reminders"
            title="Text reminders before it"
            description="You can turn them off until the appointment starts."
            checked={reminders}
            onChange={setReminders}
          />
        </div>
      </DialogBody>
      <DialogFooter className="flex-row items-center">
        <span className="mr-auto flex items-center gap-2">
          <span className="text-sm text-text-muted">Status:</span>
          <Select value={status} onValueChange={(s) => setStatus(s as "confirmed" | "scheduled")}>
            <SelectTrigger className="w-36" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="confirmed">
                <AppointmentStatusLabel status="confirmed" />
              </SelectItem>
              <SelectItem value="scheduled">
                <AppointmentStatusLabel status="scheduled" />
              </SelectItem>
            </SelectContent>
          </Select>
        </span>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} loading={book.isPending} disabled={!ready} title={!contact ? "Select a contact" : undefined}>
          Book appointment
        </Button>
      </DialogFooter>
    </>
  );
}

/** One choice of what the contact is texted (ACL-504): what it is, and a switch. */
function TextChoice({
  id, title, description, checked, onChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
      <div>
        <p id={`${id}-label`} className="text-xs font-medium text-text">
          {title}
        </p>
        <p className="text-xs text-text-muted">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-labelledby={`${id}-label`} />
    </div>
  );
}

/** A local `datetime-local` value, the next quarter hour from now or `offsetMinutes` after it. */
function nextQuarter(offsetMinutes = 0): string {
  return localInput(new Date(Math.ceil(Date.now() / 900_000) * 900_000 + offsetMinutes * 60_000));
}

/** A `datetime-local` value for a local instant. */
function localInput(d: Date): string {
  return `${ymd(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Where a block starts when the calendar was clicked: the time clicked, or 9 AM on the day. */
function pickedStart(picked: PickedTime): Date {
  const d = new Date(picked.at);
  if (!picked.withTime) d.setHours(9, 0, 0, 0);
  return d;
}

function BlockForm({ picked, onClose }: { picked: PickedTime | null; onClose: () => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  // Whose time: the server searches the users (ACL-491), which once stopped at the first 50 by
  // email; the calendars are few and filtered here.
  const [query, setQuery] = React.useState("");
  const people = useUserSearch(query, { is_active: true }, { enabled: supervisor });
  const calendars = useCalendars({}, { enabled: supervisor });
  const create = useCreateCalendarBlock();
  const self: ComboboxOption | null = user
    ? { value: `user:${user.id}`, label: staffName(user), hint: user.email, group: "Users" }
    : null;
  const [owner, setOwner] = React.useState<string>(self?.value ?? "");
  // What was picked, kept with its name: a person found by a search is not in the list once it closes.
  const [ownerOption, setOwnerOption] = React.useState<ComboboxOption | null>(self);
  const needle = query.trim().toLowerCase();
  const ownerOptions: ComboboxOption[] = supervisor
    ? [
        ...withPicked(
          self ? [self] : [],
          people.users.map((u) => ({ value: `user:${u.id}`, label: staffName(u), hint: u.email, group: "Users" })),
          people.searching,
          (o) => o.value,
        ),
        ...(calendars.data ?? [])
          .filter((c) => !needle || c.name.toLowerCase().includes(needle))
          .map((c) => ({ value: `calendar:${c.id}`, label: c.name, group: "Calendars" })),
      ]
    : self ? [self] : [];
  const [title, setTitle] = React.useState("");
  const [from, setFrom] = React.useState(() => (picked ? localInput(pickedStart(picked)) : nextQuarter()));
  const [to, setTo] = React.useState(() =>
    picked ? localInput(new Date(pickedStart(picked).getTime() + 30 * 60_000)) : nextQuarter(30),
  );
  const [error, setError] = React.useState<string | null>(null);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const backwards = Boolean(from && to && to <= from);

  const submit = async () => {
    if (!owner || !from || !to || backwards) return;
    setError(null);
    const [kind, id] = owner.split(":");
    try {
      await create.mutateAsync({
        user_id: kind === "user" ? Number(id) : null,
        calendar_id: kind === "calendar" ? Number(id) : null,
        title: title.trim() || null,
        start: new Date(from).toISOString(),
        end: new Date(to).toISOString(),
      });
      toast.success("Time blocked off", title.trim() || "Nothing can be booked in it.");
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not block the time. Please try again.");
    }
  };

  return (
    <>
      <DialogBody className="space-y-4">
        <p className="text-sm text-text-muted">
          Going on vacation? Taking some time off? Block off time so nothing can be booked in it. Appointments already there
          stay on the calendar.
        </p>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="User / Calendar" htmlFor="block-owner" hint={supervisor ? "A person's block keeps them busy on every calendar; a calendar's closes that calendar." : "You block off your own time."}>
          <Combobox
            label="User / Calendar"
            options={ownerOptions}
            value={owner || null}
            picked={ownerOption}
            onChange={(next) => {
              if (next === null) return;
              setOwner(next);
              setOwnerOption(ownerOptions.find((o) => o.value === next) ?? null);
            }}
            onSearch={supervisor ? setQuery : undefined}
            searchedFor={people.searchedFor}
            loading={people.loading}
            footer={supervisor ? <UserSearchMore search={people} /> : undefined}
            placeholder="Pick a person or a calendar"
            clearLabel={null}
            disabled={!supervisor}
            modal
          />
        </Field>
        <Field label="Title" htmlFor="block-title">
          <Input value={title} maxLength={200} placeholder="(eg) Vacation" onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="space-y-2 rounded-lg bg-gray-2 p-3">
          <p className="text-xs text-text-muted">Times in your timezone: {zone.replace(/_/g, " ")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Start time" htmlFor="block-from">
              <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="End time" htmlFor="block-to" error={backwards ? "Ends before it starts" : undefined}>
              <Input type="datetime-local" value={to} invalid={backwards} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} loading={create.isPending} disabled={!owner || !from || !to || backwards}>
          Block time
        </Button>
      </DialogFooter>
    </>
  );
}
